import { ORPCError } from '@orpc/client'

function isOutOfScope(error: unknown): boolean {
  return (
    error instanceof ORPCError && (error.status === 404 || error.status === 403)
  )
}

/**
 * The tenant to send a user to when the host gives none. Tries the candidate
 * ids in order (active tenant first, then memberships), skipping duplicates
 * and ids the backend reports as missing/forbidden (stale active tenant,
 * removed membership). A platform role (admin, technician) then falls back to the first tenant overall. Any
 * other error propagates.
 */
export async function resolveFallbackTenant<TenantT>({
  candidateIds,
  getTenant,
  firstTenant,
  hasPlatformRole,
}: {
  candidateIds: readonly (string | null)[]
  getTenant: (id: string) => Promise<TenantT>
  firstTenant: () => Promise<TenantT | null>
  /** a platform admin or a technician */
  hasPlatformRole: boolean
}): Promise<TenantT | null> {
  const seen = new Set<string>()
  for (const id of candidateIds) {
    if (id === null || seen.has(id)) {
      continue
    }
    seen.add(id)
    try {
      return await getTenant(id)
    } catch (error) {
      if (!isOutOfScope(error)) {
        throw error
      }
    }
  }
  return hasPlatformRole ? firstTenant() : null
}
